import type { AppFilterClause, AppFilterModel, AppFilterOperator } from '../../components/tables/AppTable.types';
import { appFilterOperators } from '../../components/tables/AppTable.filters';

// Version 1 codes are part of the URL contract: keep existing meanings stable.
const scalarCodes = {
    text: 't',
    number: 'n',
    date: 'd',
    dateTime: 'D',
    boolean: 'b',
    lookup: 'l',
} as const;

const operatorCodes = {
    contains: 'c',
    equals: 'e',
    in: 'i',
    startsWith: 's',
    isEmpty: 'z',
    notEquals: 'ne',
    greaterThan: 'gt',
    atLeast: 'g',
    lessThan: 'lt',
    atMost: 'm',
    on: 'o',
    before: 'b',
    onOrBefore: 'ob',
    after: 'a',
    onOrAfter: 'oa',
} as const;

const scalarTypesByCode = new Map(Object.entries(scalarCodes).map(([type, code]) => [code as string, type]));
const operatorsByCode = new Map(Object.entries(operatorCodes).map(([operator, code]) => [code as string, operator]));

function calendarDate(value: Date): string {
    const year = String(value.getFullYear()).padStart(4, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/** A versioned UTF-8 Base64url payload; column IDs and value types remain stable. */
export function encodeReportFilterModel(model: AppFilterModel): string {
    const clauses = model.filters.filter((filter): filter is AppFilterClause => filter.scalarType !== 'draft').map((filter) => {
        const clause: unknown[] = [filter.columnId, scalarCodes[filter.scalarType], `${filter.negated ? '!' : ''}${operatorCodes[filter.operator]}`];

        if (filter.operator !== 'isEmpty') {
            clause.push(filter.scalarType === 'date' ? calendarDate(filter.value) : filter.value);
        }

        return clause;
    });
    const bytes = new TextEncoder().encode(JSON.stringify([model.logic === 'and' ? 'a' : 'o', clauses]));
    let binary = '';

    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }

    return `1.${btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
}

export function decodeReportFilterModel(serialized: string | null): AppFilterModel | undefined {
    if (!serialized || !/^1\.[A-Za-z0-9_-]+$/.test(serialized)) {
        return undefined;
    }

    try {
        const binary = atob(serialized.slice(2).replace(/-/g, '+').replace(/_/g, '/'));
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        const payload = JSON.parse(
            new TextDecoder('utf-8', {
                fatal: true,
            }).decode(bytes),
        );

        if (
            !Array.isArray(payload) ||
            payload.length !== 2 ||
            (payload[0] !== 'a' && payload[0] !== 'o') ||
            !Array.isArray(payload[1])
        ) {
            return undefined;
        }

        const filters = payload[1].flatMap((clause: unknown): AppFilterClause[] => {
            if (
                !Array.isArray(clause) ||
                typeof clause[0] !== 'string' ||
                typeof clause[1] !== 'string' ||
                typeof clause[2] !== 'string'
            ) {
                return [];
            }

            const scalarType = scalarTypesByCode.get(clause[1]) as AppFilterClause['scalarType'] | undefined;
            const negated = clause[2].startsWith('!');
            const operator = operatorsByCode.get(negated ? clause[2].slice(1) : clause[2]);

            if (!scalarType || !operator || clause.length !== (operator === 'isEmpty' ? 3 : 4)) {
                return [];
            }

            const operators: readonly string[] = appFilterOperators[scalarType];

            if (!operators.includes(operator)) {
                return [];
            }
            const clauseOperator = operator as AppFilterOperator;

            if (clauseOperator === 'isEmpty') {
                if (scalarType === 'boolean') {
                    return [];
                }
                return [
                    {
                        columnId: clause[0],
                        scalarType,
                        operator: clauseOperator,
                        ...(negated ? { negated: true } : {}),
                    },
                ];
            }

            let value = clause[3];

            if (scalarType === 'lookup' && clauseOperator === 'in') {
                if (!Array.isArray(value) || !value.every(item => item === null
                    || typeof item === 'string' || typeof item === 'boolean'
                    || (typeof item === 'number' && Number.isFinite(item)))) {
                    return [];
                }
            } else if (scalarType === 'date' || scalarType === 'dateTime') {
                if (typeof value !== 'string') {
                    return [];
                }

                if (scalarType === 'date') {
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                        return [];
                    }

                    const date = new Date(value + 'T00:00:00');

                    if (Number.isNaN(date.getTime()) || calendarDate(date) !== value) {
                        return [];
                    }

                    value = date;
                } else {
                    value = new Date(value);
                }

                if (Number.isNaN(value.getTime())) {
                    return [];
                }
            } else if (scalarType === 'text' && typeof value !== 'string') {
                return [];
            } else if (scalarType === 'number' && (typeof value !== 'number' || !Number.isFinite(value))) {
                return [];
            } else if (scalarType === 'boolean' && typeof value !== 'boolean') {
                return [];
            } else if (
                scalarType === 'lookup' &&
                value !== null &&
                !['string', 'number', 'boolean'].includes(typeof value)
            ) {
                return [];
            }

            return [
                {
                    columnId: clause[0],
                    scalarType,
                    operator: clauseOperator,
                    ...(negated ? { negated: true } : {}),
                    value,
                } as AppFilterClause,
            ];
        });

        return {
            logic: payload[0] === 'a' ? 'and' : 'or',
            filters,
        };
    } catch {
        return undefined;
    }
}

/** Only the versioned compact parameter is supported. */
export function readReportFilterParams(params: URLSearchParams): AppFilterModel | undefined {
    return decodeReportFilterModel(params.get('f'));
}

/** Clone parameters to preserve unrelated query state and remove obsolete JSON. */
export function writeReportFilterParams(params: URLSearchParams, model: AppFilterModel): URLSearchParams {
    const nextParams = new URLSearchParams(params);
    nextParams.delete('filter');
    nextParams.delete('f');

    if (model.filters.some((filter) => filter.scalarType !== 'draft')) {
        nextParams.set('f', encodeReportFilterModel(model));
    }

    return nextParams;
}
